import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Skeleton,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import PageHeader from '@/components/layout/page-header'
import { CreateDeviceDialog, DeviceCard } from '@/features/devices/components'
import { useGetDevices } from '@/features/devices/hooks'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/dashboard/devices/')({
  head: () => ({ meta: [{ title: localizedPageTitle('devices') }] }),
  component: DevicesPage,
})

function DevicesPage() {
  useDocumentTitle('devices')
  const { t } = useTranslation('devices')
  const [searchQuery, setSearchQuery] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const { data: devices, isLoading } = useGetDevices()

  const filteredDevices =
    devices?.filter(
      (device) =>
        device.deviceIdent.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (device.name?.toLowerCase().includes(searchQuery.toLowerCase()) ??
          false) ||
        (device.vehicle?.vinNumber
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ??
          false),
    ) ?? []

  return (
    <div className="flex-1 space-y-4">
      <PageHeader title={t('title')} subtitle={t('subtitle')} hideBackButton />

      <Card>
        <CardHeader>
          <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>{t('allDevices')}</CardTitle>
              <CardDescription>
                {devices?.length ?? 0} {t('totalDevices')}
              </CardDescription>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="relative w-full md:w-64">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder={t('searchDevices')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-11 pl-9"
                />
              </div>

              <Button
                className="primary-bg-gradient w-full whitespace-nowrap sm:w-auto"
                size="lg"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="size-4" />
                {t('addDevice')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : filteredDevices.length > 0 ? (
            <div className="space-y-3">
              {filteredDevices.map((device) => (
                <DeviceCard key={device.id} device={device} />
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <p className="text-muted-foreground text-sm">
                {t('noDevicesFound')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateDeviceDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  )
}
